import { colors, icons, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { FC } from 'react'
import { AnswerRow, AnswerTone } from '../../model/review-answers'

const BAR_COLOR: Record<AnswerTone, string> = {
	correct: colors.greenishCyan,
	wrong: colors.strongPink30,
	neutral: colors.indigoBlue30,
}

const List = styled.ul`
	display: flex;
	flex-direction: column;
	gap: 28px;
	list-style: none;
	margin: 0;
	padding: 0;
`

const Answer = styled.div`
	align-items: center;
	color: ${colors.blackish};
	display: flex;
	font-size: 20px;
	font-weight: 500;
	gap: 12px;
	line-height: 1.3;
	margin-bottom: 10px;

	${ui.responsive.desktopFHD} {
		font-size: 24px;
	}
`

const Check = styled.span`
	align-items: center;
	background: ${colors.greenishCyan};
	border-radius: 50%;
	color: ${colors.white};
	display: flex;
	flex: none;
	height: 24px;
	justify-content: center;
	width: 24px;

	svg {
		width: 12px;
	}
`

const Measure = styled.div`
	align-items: center;
	display: flex;
	gap: 20px;
`

const Bar = styled.div`
	background: ${colors.brightGray};
	border-radius: 8px;
	flex: 1;
	height: 28px;
	overflow: hidden;

	${ui.responsive.desktopFHD} {
		height: 36px;
	}
`

const Fill = styled.div<{ tone: AnswerTone; percent: number }>`
	background: ${props => BAR_COLOR[props.tone]};
	border-radius: 8px;
	height: 100%;
	transition: width 0.4s ease-in-out;
	width: ${props => props.percent}%;

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`

const Percent = styled.div`
	color: ${colors.black};
	flex: none;
	font-size: 24px;
	font-weight: 700;
	text-align: right;
	width: 4ch;

	${ui.responsive.desktopFHD} {
		font-size: 32px;
	}
`

type Props = {
	rows: AnswerRow[]
	/** Said for the correct answer's mark, which is a picture. */
	correctLabel: string
}

/** The answers of a question with a full-width bar each, and the share that picked it. */
const AnswerBars: FC<Props> = ({ rows, correctLabel }) => (
	<List>
		{rows.map(row => (
			<li key={row.id}>
				<Answer>
					{row.marked ? (
						<Check role="img" aria-label={correctLabel}>
							<icons.IconChecked />
						</Check>
					) : null}
					{row.answer}
				</Answer>
				<Measure>
					<Bar
						role="progressbar"
						aria-label={row.answer}
						aria-valuemin={0}
						aria-valuemax={100}
						aria-valuenow={row.percent}
						aria-valuetext={`${row.percent}%`}
					>
						<Fill tone={row.tone} percent={row.percent} />
					</Bar>
					<Percent>{row.percent}%</Percent>
				</Measure>
			</li>
		))}
	</List>
)

export default AnswerBars
